import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Square, Moon, Volume2, Radio, MousePointer2, Clock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { reportSession } from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { useAuth } from "@/context/AuthContext";
import { GUEST_LIMIT_MS, GUEST_USAGE_KEY, readGuestUsage, recordGuestUsage } from "@/lib/guestUsage";

const fmt = (s) => {
  const h = String(Math.floor(s / 3600)).padStart(2, "0");
  const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
  const sec = String(s % 60).padStart(2, "0");
  return `${h}:${m}:${sec}`;
};

export default function JigglerTool() {
  const { live, setLive, registerStop } = useSession();
  const { user, loading: authLoading, requireSignIn } = useAuth();
  const authRef = useRef({ user, loading: authLoading });
  authRef.current = { user, loading: authLoading };
  const [guestRemaining, setGuestRemaining] = useState(() => Math.ceil((GUEST_LIMIT_MS - readGuestUsage()) / 1000));
  const [elapsed, setElapsed] = useState(0);
  const [bgMode, setBgMode] = useState(true);
  const [autoStop, setAutoStop] = useState("");
  const [engine, setEngine] = useState({ audio: false, wakelock: false, events: false });
  const [hidden, setHidden] = useState(false);

  const refs = useRef({
    timer: null,
    events: null,
    audioCtx: null,
    osc: null,
    wakeLock: null,
    startedAt: 0,
    stopAt: 0,
    guestTickAt: 0,
    signedIn: false,
  });

  const settleGuestUsage = useCallback(() => {
    const r = refs.current;
    const now = Date.now();
    const spent = r.guestTickAt && !r.signedIn
      ? recordGuestUsage(now - r.guestTickAt) : readGuestUsage();
    r.guestTickAt = r.startedAt ? now : 0;
    r.signedIn = Boolean(authRef.current.user);
    setGuestRemaining(Math.ceil((GUEST_LIMIT_MS - spent) / 1000));
    return spent;
  }, []);

  const stop = useCallback((reason) => {
    const r = refs.current;
    if (!r.startedAt) return;
    settleGuestUsage();
    clearInterval(r.timer);
    clearInterval(r.events);
    if (r.osc) { try { r.osc.stop(); } catch (e) {} }
    if (r.audioCtx) { try { r.audioCtx.close(); } catch (e) {} }
    if (r.wakeLock) { try { r.wakeLock.release(); } catch (e) {} }
    r.timer = null;
    r.events = null;
    r.audioCtx = null;
    r.osc = null;
    r.wakeLock = null;
    r.stopAt = 0;
    setEngine({ audio: false, wakelock: false, events: false });
    setLive(false);
    const seconds = Math.round((Date.now() - r.startedAt) / 1000);
    if (seconds > 3) {
      reportSession(seconds / 60);
      if (reason !== "auth") toast.success(
        reason === "auto" ? "Auto-stop reached" : "Session ended",
        { description: `${fmt(seconds)} of green status banked.` }
      );
    }
    r.startedAt = 0;
    r.guestTickAt = 0;
  }, [setLive, settleGuestUsage]);

  const checkGuestLimit = useCallback(() => {
    const used = settleGuestUsage();
    if (!authRef.current.user && used >= GUEST_LIMIT_MS) {
      stop("auth");
      requireSignIn();
      return true;
    }
    return false;
  }, [settleGuestUsage, stop, requireSignIn]);

  const acquireWakeLock = useCallback(async () => {
    try {
      if ("wakeLock" in navigator) {
        const startedAt = refs.current.startedAt;
        const lock = await navigator.wakeLock.request("screen");
        if (!startedAt || refs.current.startedAt !== startedAt) {
          await lock.release();
          return;
        }
        refs.current.wakeLock = lock;
        setEngine((e) => ({ ...e, wakelock: true }));
        lock.addEventListener("release", () => {
          if (refs.current.wakeLock === lock) {
            refs.current.wakeLock = null;
            setEngine((e) => ({ ...e, wakelock: false }));
          }
        });
      }
    } catch (e) {
      setEngine((en) => ({ ...en, wakelock: false }));
    }
  }, []);

  const startAudio = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 140;
      osc.type = "sine";
      gain.gain.value = 0.0001;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      refs.current.audioCtx = ctx;
      refs.current.osc = osc;
      setEngine((e) => ({ ...e, audio: true }));
    } catch (e) {
      setEngine((en) => ({ ...en, audio: false }));
    }
  }, []);

  const dispatchActivity = () => {
    const x = 400 + Math.random() * 600;
    const y = 300 + Math.random() * 300;
    document.dispatchEvent(new MouseEvent("mousemove", { clientX: x, clientY: y, bubbles: true }));
    document.dispatchEvent(new PointerEvent("pointermove", { clientX: x, clientY: y, bubbles: true }));
  };

  const start = useCallback(async () => {
    const r = refs.current;
    if (r.startedAt || authRef.current.loading) return;
    if (checkGuestLimit()) return;
    r.startedAt = Date.now();
    r.guestTickAt = r.startedAt;
    r.signedIn = Boolean(authRef.current.user);
    setElapsed(0);
    setLive(true);

    dispatchActivity();
    setEngine((e) => ({ ...e, events: true }));
    r.events = setInterval(() => {
      if (checkGuestLimit()) return;
      dispatchActivity();
      if (r.stopAt && Date.now() >= r.stopAt) stop("auto");
    }, 25000);

    r.timer = setInterval(() => {
      if (checkGuestLimit()) return;
      const s = Math.round((Date.now() - refs.current.startedAt) / 1000);
      setElapsed(s);
      if (r.stopAt && Date.now() >= r.stopAt) stop("auto");
    }, 1000);

    if (bgMode) {
      startAudio();
      await acquireWakeLock();
    }
    if (!r.startedAt) return;
    toast.success("Session live — your dot is green", {
      description: "Keep this tab open. You can switch windows freely.",
    });
  }, [bgMode, stop, startAudio, acquireWakeLock, setLive, checkGuestLimit]);

  const toggleAutoStop = (val) => {
    setAutoStop(val);
    if (!val) {
      refs.current.stopAt = 0;
      return;
    }
    const [h, m] = val.split(":").map(Number);
    const target = new Date();
    target.setHours(h, m, 0, 0);
    if (target.getTime() <= Date.now()) target.setDate(target.getDate() + 1);
    refs.current.stopAt = target.getTime();
  };

  useEffect(() => {
    if (!live) return undefined;
    const onVis = () => {
      if (checkGuestLimit()) return;
      setHidden(document.hidden);
      if (!document.hidden && refs.current.wakeLock == null && bgMode) acquireWakeLock();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [live, bgMode, acquireWakeLock, checkGuestLimit]);

  useEffect(() => {
    if (live) checkGuestLimit();
  }, [user, live, checkGuestLimit]);

  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== GUEST_USAGE_KEY) return;
      const used = readGuestUsage();
      setGuestRemaining(Math.ceil((GUEST_LIMIT_MS - used) / 1000));
      // Do not write usage in response to another tab's write (avoids event loops).
      if (refs.current.startedAt && !authRef.current.user && used >= GUEST_LIMIT_MS) {
        stop("auth");
        requireSignIn();
      }
    };
    const onPageHide = () => stop("unmount");
    window.addEventListener("storage", onStorage);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [requireSignIn, stop]);

  useEffect(() => {
    registerStop(stop);
    return () => {
      stop("unmount");
      registerStop(null);
    };
  }, [registerStop, stop]);

  const stopTimeLabel = autoStop
    ? new Date(refs.current.stopAt || Date.now()).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    : "--:--";

  const engineRows = [
    { icon: Volume2, key: "audio", label: "Silent audio loop", ok: engine.audio },
    { icon: Moon, key: "wakelock", label: "Wake Lock (screen awake)", ok: engine.wakelock },
    { icon: Radio, key: "events", label: "Pointer activity engine", ok: engine.events },
  ];

  return (
    <div
      id="tool"
      data-testid="jiggler-tool"
      className="relative rounded-3xl glass shadow-[0_0_60px_rgba(0,255,102,0.12)] overflow-hidden"
    >
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
        <div className="flex items-center gap-2.5">
          <span
            className={`w-2.5 h-2.5 rounded-full ${live ? "bg-neon pulse-dot" : "bg-dim"}`}
            data-testid="session-status-dot"
          />
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-mist">
            {live ? "Session live" : "Dormant"}
          </span>
        </div>
        <span
          className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border ${
            live ? "border-neon/40 text-neon bg-neon/10" : "border-white/10 text-dim"
          }`}
          data-testid="session-status-badge"
        >
          {live ? "Green" : "Standby"}
        </span>
      </div>

      <div className="px-6 pt-7 pb-4">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-dim mb-2">Session duration</p>
        <p
          className="font-mono font-bold text-[36px] min-[400px]:text-[48px] sm:text-[64px] lg:text-[48px] xl:text-[64px] leading-none text-white tabular-nums tracking-normal"
          data-testid="session-timer"
        >
          {fmt(elapsed)}
        </p>
        {!user && !authLoading && <p className="mt-3 text-xs text-mist" data-testid="guest-time-remaining">
          {guestRemaining > 0 ? `Guest time left · ${fmt(guestRemaining)}` : "Guest hour used · sign in to continue"}
        </p>}
        {hidden && live && (
          <p className="mt-3 font-mono text-[11px] text-neon flex items-center gap-2" data-testid="background-active-note">
            <span className="w-1.5 h-1.5 bg-neon rounded-full blink" />
            Tab in background — engine still running
          </p>
        )}
      </div>

      <div className="px-6">
        <div className="relative h-14 rounded-xl bg-ink border border-white/5 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.35]" style={{ backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,0.04) 0 1px, transparent 1px 28px)" }} />
          {live ? (
            <div className="tool-activity-sweep absolute inset-0" aria-hidden="true">
              <span
                className="absolute top-1/2 -mt-2 w-4 h-4 rounded-full bg-neon"
                data-testid="tool-moving-cursor"
              />
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center gap-2 text-dim text-xs font-mono uppercase tracking-[0.2em]">
              <MousePointer2 size={13} />
              Idle — no activity simulated
            </div>
          )}
        </div>
      </div>

      <div className="px-6 pt-6">
        <AnimatePresence mode="wait">
          {live ? (
            <motion.button
              key="stop"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              onClick={() => stop("manual")}
              data-testid="stop-session-button"
              className="w-full inline-flex items-center justify-center gap-2.5 rounded-2xl border border-white/15 bg-white/5 text-white font-semibold py-4 transition-colors hover:border-neon/60 hover:text-neon"
            >
              <Square size={17} strokeWidth={2.5} />
              Stop session
            </motion.button>
          ) : (
            <motion.button
              key="start"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              onClick={start}
              disabled={authLoading}
              data-testid="start-session-button"
              className="w-full inline-flex items-center justify-center gap-2.5 rounded-2xl bg-neon text-ink font-bold text-lg py-4 transition-colors duration-200 hover:bg-neonhover disabled:opacity-50"
            >
              <Play size={19} strokeWidth={2.5} />
              {authLoading ? "Checking session…" : !user && guestRemaining === 0 ? "Sign in to continue" : "Start free session"}
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      <div className="px-6 pt-6 grid grid-cols-1 gap-4">
        <div className="flex items-center justify-between gap-2 rounded-xl border border-white/5 bg-white/[0.02] px-3 sm:px-4 py-3.5">
          <div className="min-w-0 flex items-center gap-2 sm:gap-3">
            <Volume2 size={16} className="text-neon" />
            <div>
              <p className="text-sm font-medium text-white">Background mode</p>
              <p className="text-xs text-dim">Anti-throttle audio + wake lock</p>
            </div>
          </div>
          <button
            role="switch"
            aria-checked={bgMode}
            onClick={() => {
              const next = !bgMode;
              setBgMode(next);
              if (live) {
                if (next) { startAudio(); acquireWakeLock(); }
                else {
                  if (refs.current.osc) { try { refs.current.osc.stop(); } catch (e) {} }
                  if (refs.current.audioCtx) { try { refs.current.audioCtx.close(); } catch (e) {} }
                  refs.current.audioCtx = null;
                  refs.current.osc = null;
                  if (refs.current.wakeLock) { try { refs.current.wakeLock.release(); } catch (e) {} }
                  refs.current.wakeLock = null;
                  setEngine({ audio: false, wakelock: false, events: true });
                }
              }
            }}
            data-testid="bg-mode-toggle"
            className={`relative shrink-0 w-12 h-7 rounded-full transition-colors ${bgMode ? "bg-neon" : "bg-raised border border-white/10"}`}
          >
            <motion.span
              layout
              className={`absolute top-1 w-5 h-5 rounded-full ${bgMode ? "left-6 bg-ink" : "left-1 bg-dim"}`}
            />
          </button>
        </div>

        <div className="flex flex-col min-[400px]:flex-row items-start min-[400px]:items-center justify-between gap-3 rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3.5">
          <div className="flex items-center gap-3">
            <Clock size={16} className="text-neon" />
            <div>
              <p className="text-sm font-medium text-white">Auto-stop at</p>
              <p className="text-xs text-dim" data-testid="auto-stop-label">
                {autoStop ? `Scheduler ON — ${stopTimeLabel}` : "Scheduler off"}
              </p>
            </div>
          </div>
          <input
            type="time"
            value={autoStop}
            onChange={(e) => toggleAutoStop(e.target.value)}
            data-testid="auto-stop-input"
            aria-label="Auto stop time"
            className="time-input bg-ink border border-white/10 rounded-lg px-3 py-2 font-mono text-sm text-white focus:outline-none focus:border-neon/60"
          />
        </div>
      </div>

      <div className="px-6 py-6">
        <div className="rounded-xl border border-white/5 divide-y divide-white/5" data-testid="engine-status-panel">
          {engineRows.map((row) => (
            <div key={row.key} className="flex items-center justify-between px-4 py-2.5">
              <span className="flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.16em] text-mist">
                <row.icon size={13} className="text-dim" />
                {row.label}
              </span>
              <span
                data-testid={`engine-status-${row.key}`}
                className={`font-mono text-[10px] uppercase tracking-[0.2em] ${row.ok ? "text-neon" : "text-dim"}`}
              >
                {row.ok ? "Active" : "Idle"}
              </span>
            </div>
          ))}
        </div>
        <p className="mt-4 flex items-center gap-2 text-[11px] text-dim" data-testid="tool-safety-note">
          <ShieldCheck size={13} className="text-neon" />
          100% Safe. No installation. No admin rights.
        </p>
      </div>
    </div>
  );
}
